// The season board (13 §13.4): every shipment carrying a season tag, against the season's
// deliver-by date. A shipment is on track when even the late end of its predicted window is on or
// before the date, at risk when only the median is, late when the median is past it. Pure.
import { dateAfter, PREDICTION_STAGES, type PredictionStage } from './prediction';

export const SEASON_RISKS = ['on_track', 'at_risk', 'late'] as const;
export type SeasonRisk = (typeof SEASON_RISKS)[number];

const DAY_MS = 86_400_000;

/** Whole days from one calendar date to another (negative when `to` is earlier). */
export function daysBetweenDates(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

export interface SeasonWindow {
  median: string;
  to: string;
  basis: 'history' | 'target';
}

/** The SLA window when nothing better is known: the shipment is due at its door-to-door target. */
export function targetSeasonWindow(collectedAt: string, targetDays: number): SeasonWindow {
  const due = dateAfter(collectedAt, targetDays);
  return { median: due, to: due, basis: 'target' };
}

/** `daysOver` is how far the late end of the window is past the date (0 when it is not). */
export function seasonRisk(
  deliverBy: string,
  window: Pick<SeasonWindow, 'median' | 'to'>,
): { risk: SeasonRisk; daysOver: number } {
  const daysOver = Math.max(0, daysBetweenDates(deliverBy, window.to));
  if (window.median > deliverBy) return { risk: 'late', daysOver };
  if (window.to > deliverBy) return { risk: 'at_risk', daysOver };
  return { risk: 'on_track', daysOver: 0 };
}

const ACTIONS: Readonly<Record<PredictionStage, string>> = {
  collected: 'Get it paid and onto the next container before the cut-off',
  loaded: 'Close and sail the container; chase the booking',
  departed: 'Watch the ETA; have customs and the advance ready',
  arrived: 'Chase customs release and the pull-out today',
  customs_released: 'Pull the container out and devan it now',
  at_hub: 'Hand it to the partner or put it on a run today',
  with_partner: 'Chase the partner for a delivery date',
};

/** What would save a shipment that sits at this stage. */
export function seasonAction(stage: PredictionStage): string {
  return ACTIONS[stage];
}

/** The stage a shipment is at, from its milestone keys: the latest one that is a stage. */
export function stageFromMilestones(keys: readonly string[]): PredictionStage {
  for (const stage of [...PREDICTION_STAGES].reverse()) if (keys.includes(stage)) return stage;
  return 'collected';
}

export interface SeasonRow {
  trackingNo: string;
  stage: PredictionStage;
  route: string;
  service: 'sea' | 'air' | 'prime';
  cityName: string;
  deliveredAt: string | null;
  collectedAt: string;
  /** The stored prediction, when there is one. */
  window: SeasonWindow | null;
  /** The door-to-door target in days, for the fallback window. */
  targetDays: number;
}

export interface SeasonRowResult {
  trackingNo: string;
  stage: PredictionStage;
  route: string;
  service: 'sea' | 'air' | 'prime';
  cityName: string;
  risk: SeasonRisk;
  predictedMedian: string;
  predictedTo: string;
  daysOver: number;
  basis: 'history' | 'target';
  action: string;
}

export interface SeasonTotals {
  tagged: number;
  delivered: number;
  onTrack: number;
  atRisk: number;
  late: number;
  deliveredInTime: number;
  deliveredLate: number;
}

export interface SeasonStageCount {
  stage: PredictionStage;
  onTrack: number;
  atRisk: number;
  late: number;
}

export const SEASON_BOARD_LIST_CAP = 100;

/**
 * Totals, the funnel by stage and the list of shipments to act on, the latest prediction first.
 * Delivered shipments count as in time or late against the date and leave the forecast.
 */
export function buildSeasonBoard(
  deliverBy: string,
  rows: readonly SeasonRow[],
  cap = SEASON_BOARD_LIST_CAP,
): {
  totals: SeasonTotals;
  byStage: SeasonStageCount[];
  atRisk: SeasonRowResult[];
  truncated: boolean;
  /** The risk of every shipment that is not yet delivered, by tracking number. */
  risks: Map<string, SeasonRisk>;
} {
  const totals: SeasonTotals = {
    tagged: rows.length,
    delivered: 0,
    onTrack: 0,
    atRisk: 0,
    late: 0,
    deliveredInTime: 0,
    deliveredLate: 0,
  };
  const stages = new Map<PredictionStage, SeasonStageCount>(
    PREDICTION_STAGES.map((stage) => [stage, { stage, onTrack: 0, atRisk: 0, late: 0 }]),
  );
  const list: SeasonRowResult[] = [];
  const risks = new Map<string, SeasonRisk>();
  for (const row of rows) {
    if (row.deliveredAt !== null) {
      totals.delivered++;
      if (row.deliveredAt.slice(0, 10) <= deliverBy) totals.deliveredInTime++;
      else totals.deliveredLate++;
      continue;
    }
    const window = row.window ?? targetSeasonWindow(row.collectedAt, row.targetDays);
    const { risk, daysOver } = seasonRisk(deliverBy, window);
    risks.set(row.trackingNo, risk);
    const stage = stages.get(row.stage) as SeasonStageCount;
    if (risk === 'on_track') {
      totals.onTrack++;
      stage.onTrack++;
      continue;
    }
    if (risk === 'at_risk') {
      totals.atRisk++;
      stage.atRisk++;
    } else {
      totals.late++;
      stage.late++;
    }
    list.push({
      trackingNo: row.trackingNo,
      stage: row.stage,
      route: row.route,
      service: row.service,
      cityName: row.cityName,
      risk,
      predictedMedian: window.median,
      predictedTo: window.to,
      daysOver,
      basis: window.basis,
      action: seasonAction(row.stage),
    });
  }
  list.sort(
    (a, b) =>
      b.predictedTo.localeCompare(a.predictedTo) || a.trackingNo.localeCompare(b.trackingNo),
  );
  return {
    totals,
    byStage: [...stages.values()].filter((s) => s.onTrack + s.atRisk + s.late > 0),
    atRisk: list.slice(0, cap),
    truncated: list.length > cap,
    risks,
  };
}

export interface SeasonCutoffFact {
  originSectorId: string;
  service: 'sea' | 'air' | 'prime';
  lastCollectDate: string;
  lastLoadDate?: string | undefined;
}

/** Days left to each cut-off from `today` (negative once passed), nearest first. */
export function cutoffCountdown(
  cutoffs: readonly SeasonCutoffFact[],
  today: string,
): {
  originSectorId: string;
  service: 'sea' | 'air' | 'prime';
  lastCollectDate: string;
  lastLoadDate: string | null;
  daysLeft: number;
}[] {
  return cutoffs
    .map((c) => ({
      originSectorId: c.originSectorId,
      service: c.service,
      lastCollectDate: c.lastCollectDate,
      lastLoadDate: c.lastLoadDate ?? null,
      daysLeft: daysBetweenDates(today, c.lastCollectDate),
    }))
    .sort(
      (a, b) =>
        a.daysLeft - b.daysLeft ||
        a.originSectorId.localeCompare(b.originSectorId) ||
        a.service.localeCompare(b.service),
    );
}
